import { useEffect, useState } from 'react';

type BrokerCell = { spread: number; swap: number; fee: string };

type BrokerRow = {
  pair: string;
  sub?: string;
  exness: BrokerCell;
  ic: BrokerCell;
  xm: BrokerCell;
};

const BASE_ROWS: BrokerRow[] = [
  {
    pair: 'EUR / USD',
    sub: 'Forex Major',
    exness: { spread: 0.0, swap: -0.5, fee: '$0' },
    ic: { spread: 0.1, swap: -0.6, fee: '$3.5' },
    xm: { spread: 0.6, swap: -0.5, fee: '$0' },
  },
  {
    pair: 'GBP / USD',
    sub: 'Forex Major',
    exness: { spread: 0.2, swap: -0.7, fee: '$0' },
    ic: { spread: 0.3, swap: -0.8, fee: '$3.5' },
    xm: { spread: 1.0, swap: -0.9, fee: '$0' },
  },
  {
    pair: 'XAU / USD',
    sub: 'Vàng',
    exness: { spread: 1.2, swap: -1.5, fee: '$0' },
    ic: { spread: 1.4, swap: -1.8, fee: '$3.5' },
    xm: { spread: 1.8, swap: -2.0, fee: '$0' },
  },
];

import { useBrokerUrl } from "@/hooks/useBrokerUrl";

function BrokerColumn({ name, cell, best }: { name: string; cell: BrokerCell; best: boolean }) {
  const url = useBrokerUrl(name);
  return (
    <div className="flex flex-1 flex-col items-center text-center">
      <p className="text-xs font-bold uppercase tracking-wider text-slate-400">{name}</p>
      <p
        className={`mt-1 whitespace-nowrap ${
          best ? 'text-xl font-black text-purple-600' : 'text-lg font-bold text-slate-800'
        }`}
      >
        {cell.spread.toFixed(1)} pips
        {best && (
          <span className="ml-1 inline-block rounded bg-purple-50 px-2 py-0.5 text-[10px] font-bold text-purple-600">
            🔥 Thấp nhất
          </span>
        )}
      </p>
      <p className="mt-1 whitespace-nowrap text-xs text-slate-500">
        Swap {cell.swap.toFixed(1)} | Phí 1 lot: {cell.fee}
      </p>
      <a
        href={url}
        className={`mt-3 whitespace-nowrap rounded-lg px-4 py-2 text-xs font-bold transition-all ${
          best
            ? 'bg-purple-600 text-white shadow-sm hover:bg-purple-700'
            : 'border border-slate-200 text-slate-700 hover:border-purple-600 hover:text-purple-600'
        }`}
      >
        Mở tài khoản
      </a>
    </div>
  );
}

export default function MarketCalendar() {
  const [rows, setRows] = useState<BrokerRow[]>(BASE_ROWS);

  useEffect(() => {
    const tick = () => {
      setRows((prev) =>
        prev.map((row) => {
          const drift = (cell: BrokerCell, spreadRange: number, swapRange: number): BrokerCell => ({
            ...cell,
            spread: Math.max(0, +(cell.spread + (Math.random() - 0.5) * spreadRange).toFixed(1)),
            swap: +(cell.swap + (Math.random() - 0.5) * swapRange).toFixed(1),
          });

          return {
            ...row,
            exness: drift(row.exness, 0.2, 0.2),
            ic: drift(row.ic, 0.2, 0.2),
            xm: drift(row.xm, 0.4, 0.3),
          };
        }),
      );
    };

    const id = setInterval(tick, 2500);
    return () => clearInterval(id);
  }, []);

  return (
    <section id="calendar" className="relative bg-slate-50 py-20 md:py-28">
      <div className="mx-auto max-w-6xl px-4">
        {/* ===== PART 1: Leaderboard So sánh Spread & Swap ===== */}
        <div className="text-center">
          <p className="mb-3 text-xs font-bold uppercase tracking-widest text-purple-600">
            So sánh chi phí thời gian thực
          </p>
          <h2 className="text-4xl font-extrabold text-slate-900">
            Minh bạch hóa chi phí giao dịch
          </h2>
          <p className="mx-auto mt-4 max-w-2xl leading-relaxed text-slate-500">
            Sử dụng công cụ ngoại hối của chúng tôi để quét và so sánh Spread/Swap từ các nhà
            môi giới hàng đầu. Đừng để phí ẩn phá hỏng chiến lược giao dịch của bạn.
          </p>
        </div>

        <div className="mt-12 flex flex-col gap-4">
          {rows.map((row) => {
            const spreads = [row.exness.spread, row.ic.spread, row.xm.spread];
            const minSpread = Math.min(...spreads);

            return (
              <div
                key={row.pair}
                className="flex flex-col items-center justify-between gap-6 rounded-2xl border border-slate-200/80 bg-white p-6 shadow-sm transition-all hover:shadow-md md:flex-row"
              >
                {/* Left: Currency Pair */}
                <div className="flex w-full flex-col items-center text-center md:w-auto md:items-start md:text-left">
                  <span className="rounded-xl bg-slate-100 px-4 py-2 text-lg font-black text-slate-900">
                    {row.pair}
                  </span>
                  {row.sub && (
                    <span className="mt-1 block text-xs text-slate-400">{row.sub}</span>
                  )}
                </div>

                {/* Middle: Brokers */}
                <div className="flex w-full flex-1 flex-col gap-6 sm:flex-row sm:items-stretch">
                  <BrokerColumn name="FxPro" cell={row.exness} best={row.exness.spread === minSpread} />
                  <BrokerColumn name="IC Markets" cell={row.ic} best={row.ic.spread === minSpread} />
                  <BrokerColumn name="XM Trading" cell={row.xm} best={row.xm.spread === minSpread} />
                </div>
              </div>
            );
          })}
        </div>

        <p className="mt-6 text-center text-xs text-slate-400">
          ⚡ Dữ liệu điểm Spread và Swap được đồng bộ trực tiếp từ API các nhà môi giới IB chính
          thức.
        </p>
      </div>
    </section>
  );
}